import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import spacing from '../spacing';

/**
 * Loading Component
 * @component
 * @description Loading indicators including spinners and overlays
 * 
 * @example
 * // Simple spinner
 * <Loading />
 * 
 * @example
 * // Full page overlay
 * <Loading overlay fullscreen />
 * 
 * @example
 * // Custom size and color
 * <Loading size={60} color="secondary" />
 */

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
`;

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'overlay' && prop !== 'fullscreen',
})(({ theme, overlay, fullscreen }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing[2],

  // The overlay tints the theme's own paper color (translucent) instead of a
  // hardcoded white, so it never paints an opaque white sheet over dark-mode
  // content — which previously hid everything behind it in dark mode.
  ...(overlay && {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(11, 18, 13, 0.75)'
      : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(2px)',
    zIndex: 1000,
  }),

  ...(fullscreen && {
    position: 'fixed',
    minHeight: '100vh',
  }),
}));

const SoccerBall = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: `3px solid ${theme.palette.primary.main}`,
  borderTopColor: 'transparent',
  animation: `${pulse} 1.5s ease-in-out infinite`,
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '60%',
    height: '60%',
    borderRadius: '50%',
    border: `2px solid ${theme.palette.primary.light}`,
  },
}));

const DotsContainer = styled(Box)({
  display: 'flex',
  gap: spacing[2],
});

const Dot = styled(Box)(({ theme, delay }) => ({
  width: spacing[3],
  height: spacing[3],
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  animation: `${pulse} 1.4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const Loading = ({
  overlay = false,
  fullscreen = false,
  size = 40,
  color = 'primary',
  variant = 'circular', // 'circular', 'soccer', 'dots'
  text,
  ...props
}) => {
  const renderLoader = () => {
    switch (variant) {
      case 'soccer':
        return <SoccerBall />;
      
      case 'dots':
        return (
          <DotsContainer>
            <Dot delay={0} />
            <Dot delay={0.2} />
            <Dot delay={0.4} />
          </DotsContainer>
        );
      
      case 'circular':
      default:
        return (
          <CircularProgress
            size={size}
            color={color}
          />
        );
    }
  };

  return (
    <Container overlay={overlay} fullscreen={fullscreen} {...props}>
      {renderLoader()}
      {text && (
        <Box
          sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
            marginTop: spacing[2],
          }}
        >
          {text}
        </Box>
      )}
    </Container>
  );
};

export default Loading;
